import { isDefined } from "@clipboard-health/util-ts";
import { type ShiftFormattedPayBreakdown } from "@src/appV2/redesign/Shift/types";
import { getShiftCurrentStage } from "@src/appV2/redesign/Shift/WorkerShift/getShiftCurrentStage";
import { HighlightedWorkerShiftCard } from "@src/appV2/redesign/Shift/WorkerShift/HighlightedCard";
import { HighlightedWorkerInterviewCard } from "@src/appV2/redesign/Shift/WorkerShift/HighlightedWorkerInterviewCard";
import { type Bookings } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useGetShiftState } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { generatePath } from "react-router-dom";

import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { SHIFT_DETAILS_FULL_PATH } from "../path";

interface HighlightedBookingSectionProps {
  highlightedBooking?: Bookings;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
  shiftPayBreakdown?: ShiftFormattedPayBreakdown;
  currentDate: Date;
}

export function HighlightedBookingSection(props: HighlightedBookingSectionProps) {
  const { highlightedBooking, shiftPayBreakdown, currentDate, workersForOverlappingShiftSlot } =
    props;

  const shiftId = highlightedBooking?.type === "shift" ? highlightedBooking.data._id : undefined;

  const { data: shiftState } = useGetShiftState(
    { shiftStateDependencies: isDefined(shiftId) ? { _id: shiftId } : undefined },
    { enabled: isDefined(shiftId) }
  );

  if (!isDefined(highlightedBooking)) {
    return null;
  }

  if (highlightedBooking.type === "shift") {
    return (
      <HighlightedWorkerShiftCard
        href={generatePath(
          SHIFT_DETAILS_FULL_PATH.replace(":shiftId", highlightedBooking.data._id)
        )}
        workplaceName={highlightedBooking.data.facility.name}
        workersForOverlappingShiftSlot={workersForOverlappingShiftSlot}
        workplaceTimezone={highlightedBooking.data.facility.tmz ?? ""}
        workerTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
        shiftStart={highlightedBooking.data.start}
        shiftEnd={highlightedBooking.data.end}
        shiftPosition={highlightedBooking.data.agentReq}
        shiftHourlyPay={shiftPayBreakdown?.hourlyPay}
        shiftTotalPay={shiftPayBreakdown?.workedBreakPay?.totalPay ?? shiftPayBreakdown?.totalPay}
        shiftTotalWithBoostPay={
          shiftPayBreakdown?.workedBreakPay?.totalWithBoostPay ??
          shiftPayBreakdown?.totalWithBoostPay
        }
        shiftStage={getShiftCurrentStage(highlightedBooking.data)}
        shiftTimeSlot={shiftPeriodToTimeSlot[highlightedBooking.data.name]}
        targetArrivalTime={highlightedBooking.data.computedArrival?.targetArrivalTime}
        currentDate={currentDate}
        isInstantPayShift={highlightedBooking.data.isInstantPay ?? false}
        isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled}
      />
    );
  }

  if (highlightedBooking.type === "interview") {
    return (
      <HighlightedWorkerInterviewCard
        interviewId={highlightedBooking.data.id}
        start={highlightedBooking.data.start}
        end={highlightedBooking.data.end}
        status={highlightedBooking.data.status}
        placementId={highlightedBooking.data.placementId}
        workerTypes={highlightedBooking.data.placement?.workerTypes ?? []}
        workplaceName={highlightedBooking.data.workplace?.name ?? ""}
        currentDate={currentDate}
      />
    );
  }

  return null;
}
